import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { websiteApi } from "../../../api/websiteApi";
import BusinessProfileQuestionsEditor from "./components/BusinessProfileQuestionsEditor";
import { DEFAULT_WEBSITE_ONBOARDING_QUESTIONS } from "../../client/StoreRequest/websiteOnboardingQuestions";
import "./OnboardingControl.css";
import "./WebsiteOnboardingControl.css";

const DEFAULT_DOMAIN_EXTENSIONS = [
  { extension: ".co.za", price: 89, currency: "ZAR", popular: true },
  { extension: ".com", price: 129.99, currency: "ZAR", popular: true },
  { extension: ".org", price: 119.99, currency: "ZAR", popular: false },
  { extension: ".net", price: 119.99, currency: "ZAR", popular: false },
  { extension: ".shop", price: 99.99, currency: "ZAR", popular: false },
  { extension: ".store", price: 99.99, currency: "ZAR", popular: false },
  { extension: ".online", price: 89.99, currency: "ZAR", popular: false },
];

const DEFAULT_DOMAIN_SETTINGS = {
  enable_availability_check: "true",
  enable_dns_config: "false",
  domain_api_provider: "godaddy",
  godaddy_api_key: "",
  godaddy_api_secret: "",
  hostafrica_api_key: "",
  auto_configure_dns: "false",
};

const DEFAULT_WEBSITE_STEPS = [
  {
    id: "package",
    name: "Packages",
    title: "Select your website package",
    description: "Choose the website package that suits your project.",
    enabled: true,
    required: true,
    order: 1,
    icon: "fa-box-open",
    color: "#4f46e5",
  },
  {
    id: "business",
    name: "Business",
    title: "Tell Us About Your Business",
    description: "Share your business details and website project requirements.",
    enabled: true,
    required: true,
    order: 2,
    icon: "fa-building",
    color: "#7c3aed",
  },
  {
    id: "domain",
    name: "Domain",
    title: "Choose your domain",
    description: "Choose a domain for your website and check availability.",
    enabled: true,
    required: true,
    order: 3,
    icon: "fa-globe",
    color: "#059669",
  },
  {
    id: "agreement",
    name: "Agreement",
    title: "Service Agreement",
    description: "Review and accept the website service agreement.",
    enabled: true,
    required: true,
    order: 4,
    icon: "fa-file-signature",
    color: "#8b5cf6",
  },
  {
    id: "invoice",
    name: "Invoice & payment",
    title: "Review your invoice",
    description: "Review the website invoice and choose how to pay.",
    enabled: true,
    required: true,
    order: 5,
    icon: "fa-file-invoice-dollar",
    color: "#0891b2",
  },
];

const WebsiteOnboardingControl = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("steps");
  const [steps, setSteps] = useState(DEFAULT_WEBSITE_STEPS);
  const [packages, setPackages] = useState([]);
  const [questions, setQuestions] = useState(DEFAULT_WEBSITE_ONBOARDING_QUESTIONS);
  const [questionsEnabled, setQuestionsEnabled] = useState(true);
  const [domainExtensions, setDomainExtensions] = useState([]);
  const [domainSettings, setDomainSettings] = useState(DEFAULT_DOMAIN_SETTINGS);
  const [editingStepId, setEditingStepId] = useState("");
  const [stepDraft, setStepDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [configResponse, packagesResponse] = await Promise.all([
        websiteApi.getAdminOnboardingConfig(),
        websiteApi.getAdminPackages(),
      ]);
      const config = configResponse.data;
      const loadedSteps = config.steps;
      if (!Array.isArray(loadedSteps) || loadedSteps.length !== 5) {
        throw new Error("Website onboarding must contain exactly five steps.");
      }
      setSteps([...loadedSteps].sort((left, right) => left.order - right.order));
      setPackages(packagesResponse.data || []);
      setQuestions(
        config.questions || DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
      );
      setQuestionsEnabled(config.questionsEnabled !== false);
      setDomainExtensions(
        config.domainExtensions || DEFAULT_DOMAIN_EXTENSIONS,
      );
      setDomainSettings(config.domainSettings || DEFAULT_DOMAIN_SETTINGS);
    } catch (loadError) {
      setError(
        loadError.response?.data?.error ||
          loadError.message ||
          "Could not load website onboarding options.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const moveStep = (index, offset) => {
    const nextIndex = index + offset;
    if (nextIndex < 0 || nextIndex >= steps.length) return;
    const reordered = [...steps];
    [reordered[index], reordered[nextIndex]] = [
      reordered[nextIndex],
      reordered[index],
    ];
    setSteps(reordered.map((step, stepIndex) => ({
      ...step,
      order: stepIndex + 1,
    })));
  };

  const startEditingStep = (step) => {
    setEditingStepId(step.id);
    setStepDraft({
      name: step.name,
      title: step.title,
      description: step.description,
      icon: step.icon,
      color: step.color,
    });
  };

  const saveStepEdit = () => {
    setSteps((current) =>
      current.map((step) =>
        step.id === editingStepId ? { ...step, ...stepDraft } : step,
      ),
    );
    setEditingStepId("");
    setStepDraft(null);
  };

  const cancelStepEdit = () => {
    setEditingStepId("");
    setStepDraft(null);
  };

  const saveConfiguration = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await websiteApi.saveOnboardingConfig({
        steps,
        questions,
        questionsEnabled,
        domainExtensions,
        domainSettings,
      });
      setSteps(response.data.steps);
      setQuestions(response.data.questions);
      setQuestionsEnabled(response.data.questionsEnabled !== false);
      setDomainExtensions(response.data.domainExtensions);
      setDomainSettings(response.data.domainSettings);
      toast.success("Website onboarding options saved.");
    } catch (saveError) {
      setError(
        saveError.response?.data?.error ||
          "Could not save website onboarding options.",
      );
    } finally {
      setSaving(false);
    }
  };

  const resetWebsiteDefaults = async () => {
    if (
      !window.confirm(
        "Reset website onboarding steps and questions to their defaults?",
      )
    ) {
      return;
    }

    setSaving(true);
    setError("");
    try {
      const response = await websiteApi.saveOnboardingConfig({
        steps: DEFAULT_WEBSITE_STEPS,
        questions: DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
        questionsEnabled: true,
        domainExtensions: DEFAULT_DOMAIN_EXTENSIONS,
        domainSettings: DEFAULT_DOMAIN_SETTINGS,
      });
      setSteps(response.data.steps);
      setQuestions(DEFAULT_WEBSITE_ONBOARDING_QUESTIONS);
      setQuestionsEnabled(true);
      setDomainExtensions(response.data.domainExtensions);
      setDomainSettings(response.data.domainSettings);
      toast.success("Website onboarding defaults restored.");
    } catch (resetError) {
      setError(
        resetError.response?.data?.error ||
          "Could not reset website onboarding defaults.",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStepEnabled = (id) => {
    const selectedStep = steps.find((step) => step.id === id);
    if (
      selectedStep?.enabled &&
      steps.filter((step) => step.enabled).length === 1
    ) {
      setError("At least one website onboarding step must remain enabled.");
      return;
    }
    setError("");
    setSteps((current) =>
      current.map((step) =>
        step.id === id ? { ...step, enabled: !step.enabled } : step,
      ),
    );
  };

  if (loading) {
    return (
      <div className="onboarding-control-loading">
        <div className="spinner" />
        <p>Loading website onboarding options...</p>
      </div>
    );
  }

  return (
    <div className="onboarding-control-page">
      <div className="onboarding-control-header">
        <div className="header-left">
          <h1>Onboarding Control Center</h1>
          <p className="subtitle">
            Manage website onboarding steps, packages, client questions, and
            domain setup.
          </p>
        </div>
        <div className="header-right">
          <button
            className="btn-reset"
            type="button"
            onClick={resetWebsiteDefaults}
            disabled={saving}
          >
            <i className="fas fa-undo" aria-hidden="true" /> Reset Defaults
          </button>
          <button
            className="btn-save"
            type="button"
            onClick={saveConfiguration}
            disabled={saving || activeTab === "packages"}
          >
            {saving ? "Saving..." : "Save Controls"}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <nav className="control-navigation-tabs website-control-tabs" aria-label="Website onboarding controls">
        <button className={`tab-btn ${activeTab === "steps" ? "active" : ""}`} onClick={() => setActiveTab("steps")}>
          ⚙️ Wizard Steps ({steps.length})
        </button>
        <button className={`tab-btn ${activeTab === "packages" ? "active" : ""}`} onClick={() => setActiveTab("packages")}>
          📦 Active Packages ({packages.filter((item) => item.IsActive).length})
        </button>
        <button className={`tab-btn ${activeTab === "questions" ? "active" : ""}`} onClick={() => setActiveTab("questions")}>
          🌐 Website Questions
        </button>
        <button className={`tab-btn ${activeTab === "domains" ? "active" : ""}`} onClick={() => setActiveTab("domains")}>
          🌐 Domain TLD Pricing ({domainExtensions.length})
        </button>
        <button className={`tab-btn ${activeTab === "domainSettings" ? "active" : ""}`} onClick={() => setActiveTab("domainSettings")}>
          🌐 Domain Settings
        </button>
      </nav>

      {activeTab === "steps" && (
        <>
          <section className="request-store-indicator-preview">
            <h4>Live Wizard Progress Indicator Preview</h4>
            <div className="step-dots-preview">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`preview-dot ${step.enabled ? "active" : "disabled"}`}
                >
                  <span
                    className="dot-num"
                    style={{
                      background: step.enabled ? step.color : "#d1d5db",
                    }}
                  >
                    <i className={`fas ${step.icon}`} aria-hidden="true" />
                  </span>
                  <span className="dot-label">{step.name}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="control-panel-section">
            <h3>Website onboarding steps</h3>
            <p className="section-desc">
              Configure the Packages, Business, Domain, Agreement, and Invoice
              &amp; Payment stages. The agreement content is managed in
              Contracts &amp; Branding.
            </p>
            <div className="steps-list website-steps-list">
              <div className="steps-header">
                <span className="col-order">Order</span>
                <span className="col-status">Status</span>
                <span className="col-step">Step Name &amp; Title</span>
                <span className="col-tiers">Available Website Packages</span>
                <span className="col-required">Required</span>
                <span className="col-actions">Actions</span>
              </div>
              {steps.map((step, index) => {
                const editing = editingStepId === step.id;
                return (
                  <article className="step-card website-step-card" key={step.id}>
                    <div className="step-col step-col-order">
                      <span className="step-number">{step.order || index + 1}</span>
                    </div>
                    <div className="step-col step-col-status">
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={step.enabled}
                          onChange={() => toggleStepEnabled(step.id)}
                          disabled={
                            step.enabled &&
                            steps.filter((item) => item.enabled).length === 1
                          }
                          title={
                            step.enabled &&
                            steps.filter((item) => item.enabled).length === 1
                              ? "At least one website step must remain enabled."
                              : undefined
                          }
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                    <div className="step-col step-col-step">
                      {editing && stepDraft ? (
                        <div className="edit-form-inline website-step-edit-form">
                          <div className="edit-row">
                            <input
                              className="edit-icon-input"
                              aria-label="Step icon class"
                              value={stepDraft.icon}
                              onChange={(event) => setStepDraft({ ...stepDraft, icon: event.target.value })}
                            />
                            <input
                              className="edit-name-input"
                              aria-label="Step label"
                              value={stepDraft.name}
                              onChange={(event) => setStepDraft({ ...stepDraft, name: event.target.value })}
                            />
                            <input
                              className="edit-color-input"
                              aria-label="Step color"
                              type="color"
                              value={stepDraft.color}
                              onChange={(event) => setStepDraft({ ...stepDraft, color: event.target.value })}
                            />
                          </div>
                          <input
                            className="edit-desc-input"
                            aria-label="Step heading"
                            value={stepDraft.title}
                            onChange={(event) => setStepDraft({ ...stepDraft, title: event.target.value })}
                          />
                          <input
                            className="edit-desc-input"
                            aria-label="Step description"
                            value={stepDraft.description}
                            onChange={(event) => setStepDraft({ ...stepDraft, description: event.target.value })}
                          />
                        </div>
                      ) : (
                        <div className="step-info">
                          <span className="step-icon" style={{ background: step.color || "#4f46e5" }}>
                            <i className={`fas ${step.icon}`} aria-hidden="true" />
                          </span>
                          <div className="step-details">
                            <span className="step-name">
                              <strong>{step.name}</strong>
                              {step.title && step.title !== step.name && ` (${step.title})`}
                            </span>
                            <span className="step-description">{step.description}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="step-col step-col-tiers">
                      <div className="tier-badges">
                        {packages.filter((item) => item.IsActive).map((item) => (
                          <span className="tier-badge website-package-badge" key={item.WebsitePackageID}>
                            {item.Name}
                          </span>
                        ))}
                        {!packages.some((item) => item.IsActive) && (
                          <span className="tier-badge all-tiers">No active packages</span>
                        )}
                      </div>
                    </div>
                    <div className="step-col step-col-required">
                      <label className="toggle toggle-small">
                        <input type="checkbox" checked disabled title="All five website onboarding steps are required." />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                    <div className="step-col step-col-actions">
                      {editing ? (
                        <>
                          <button type="button" className="action-btn save" onClick={saveStepEdit} aria-label={`Save ${step.name}`}>✓</button>
                          <button type="button" className="action-btn cancel" onClick={cancelStepEdit} aria-label={`Cancel editing ${step.name}`}>✕</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="action-btn move" onClick={() => moveStep(index, -1)} disabled={index === 0} aria-label={`Move ${step.name} up`}>▲</button>
                          <button type="button" className="action-btn move" onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1} aria-label={`Move ${step.name} down`}>▼</button>
                          <button type="button" className="action-btn edit" onClick={() => startEditingStep(step)} aria-label={`Edit ${step.name}`}>✎</button>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      )}

      {activeTab === "packages" && (
        <section className="control-panel-section">
          <div className="website-control-section-heading">
            <div>
              <h3>Active website packages</h3>
              <p className="section-desc">These website-specific packages are shown in the client wizard.</p>
            </div>
            <button className="btn-add" type="button" onClick={() => navigate("/admin/website-packages")}>
              Manage Website Packages
            </button>
          </div>
          <div className="package-grid-compact">
            {packages.filter((item) => item.IsActive).map((pkg) => {
              const features = Array.isArray(pkg.Features)
                ? pkg.Features
                : typeof pkg.Features === "string"
                  ? JSON.parse(pkg.Features)
                  : [];
              return (
                <div
                  key={pkg.WebsitePackageID}
                  className={`package-card-compact ${pkg.IsPopular ? "popular" : ""}`}
                >
                  {pkg.IsPopular && <div className="popular-badge-compact">POPULAR</div>}
                  <div className="package-card-header-compact">
                    <h4>{pkg.Name}</h4>
                    <span className="package-tier-compact">WEBSITE</span>
                  </div>
                  <div className="package-price-compact">
                    {pkg.IsQuoteOnly
                      ? "Custom quote"
                      : `R${Number(pkg.Price || 0).toLocaleString("en-ZA")}`}
                    {!pkg.IsQuoteOnly && <span>/{pkg.PriceLabel || "once-off"}</span>}
                  </div>
                  {pkg.Description && <p className="website-package-description">{pkg.Description}</p>}
                  <ul className="package-features-compact">
                    {features.slice(0, 4).map((feature, featureIndex) => (
                      <li key={`${pkg.WebsitePackageID}-${featureIndex}`}>
                        <i className="fas fa-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {!packages.some((item) => item.IsActive) && <p>No active website packages are configured.</p>}
          </div>
        </section>
      )}

      {activeTab === "questions" && (
        <section className="control-panel-section">
          <div className="website-question-visibility">
            <div>
              <h3>Show questions in the client wizard</h3>
              <p className="section-desc">
                When enabled, website project questions appear before the
                configurable onboarding steps. Answers are included with the
                submitted website request.
              </p>
            </div>
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={questionsEnabled}
                onChange={(event) => setQuestionsEnabled(event.target.checked)}
              />
              <span>{questionsEnabled ? "Enabled" : "Disabled"}</span>
            </label>
          </div>
          <BusinessProfileQuestionsEditor
            sections={questions}
            onChange={setQuestions}
            eyebrow="WEBSITE CLIENT ONBOARDING"
            title="Website project questions"
            description="Configure the project questions collected from website clients. These are stored separately from store onboarding."
            questionLimit={30}
          />
        </section>
      )}

      {activeTab === "domains" && (
        <section className="control-panel-section">
          <h3>Domain Extensions &amp; Pricing Controls</h3>
          <p className="section-desc">
            Configure default TLD prices shown to clients in Step 2 (Domain
            Setup). Enter prices before VAT; clients see the price with 15% VAT
            included.
          </p>
          <div className="domain-controls-grid">
            {domainExtensions.map((ext, index) => (
              <div key={ext.extension} className="domain-card-control">
                <div className="ext-badge">{ext.extension}</div>
                <div className="form-group-inline">
                  <label>Price before VAT:</label>
                  <input
                    type="number"
                    value={ext.price}
                    onChange={(event) => {
                      const price = parseFloat(event.target.value);
                      setDomainExtensions((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, price: Number.isNaN(price) ? 0 : price }
                            : item,
                        ),
                      );
                    }}
                    step="0.01"
                    min="0"
                  />
                </div>
                <p>
                  Client price incl. 15% VAT: R
                  {(
                    Math.round(
                      (Number(ext.price || 0) * 1.15 + Number.EPSILON) * 100,
                    ) / 100
                  ).toFixed(2)}
                </p>
                <label className="checkbox-control">
                  <input
                    type="checkbox"
                    checked={Boolean(ext.popular)}
                    onChange={() =>
                      setDomainExtensions((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, popular: !item.popular }
                            : item,
                        ),
                      )
                    }
                  />
                  <span>Mark as ⭐ Popular</span>
                </label>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeTab === "domainSettings" && (
        <section className="control-panel-section">
          <h3>Domain Configuration Settings</h3>
          <p className="section-desc">
            Configure domain availability checking and DNS automation settings.
            These settings control the Domain step in the website onboarding
            flow and are separate from store onboarding.
          </p>
          <div className="domain-settings-grid">
            <div className="setting-card">
              <h4>🔍 Availability Check</h4>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={domainSettings.enable_availability_check === "true"}
                  onChange={(event) =>
                    setDomainSettings({
                      ...domainSettings,
                      enable_availability_check: event.target.checked
                        ? "true"
                        : "false",
                    })
                  }
                />
                <span>Enable Domain Availability Check</span>
              </label>
              <small>
                When disabled, users can enter any domain without validation.
                This bypasses GoDaddy/HostAfrica API calls.
              </small>
            </div>
            <div className="setting-card">
              <h4>⚙️ DNS Configuration</h4>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={domainSettings.enable_dns_config === "true"}
                  onChange={(event) =>
                    setDomainSettings({
                      ...domainSettings,
                      enable_dns_config: event.target.checked ? "true" : "false",
                    })
                  }
                />
                <span>Enable DNS Configuration</span>
              </label>
              <small>
                Enable automatic DNS configuration for registered domains.
              </small>
            </div>
            <div className="setting-card">
              <h4>🔄 Auto-Configure DNS</h4>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={domainSettings.auto_configure_dns === "true"}
                  onChange={(event) =>
                    setDomainSettings({
                      ...domainSettings,
                      auto_configure_dns: event.target.checked ? "true" : "false",
                    })
                  }
                  disabled={domainSettings.enable_dns_config !== "true"}
                />
                <span>Auto-Configure DNS Records</span>
              </label>
              <small>
                Automatically set up A, CNAME, and MX records for the domain.
                Only available when DNS Configuration is enabled.
              </small>
            </div>
            <div className="setting-card">
              <h4>🏢 API Provider</h4>
              <select
                value={domainSettings.domain_api_provider || "godaddy"}
                onChange={(event) =>
                  setDomainSettings({
                    ...domainSettings,
                    domain_api_provider: event.target.value,
                  })
                }
              >
                <option value="godaddy">GoDaddy</option>
                <option value="hostafrica">HostAfrica</option>
                <option value="none">None (Simulation Mode)</option>
              </select>
              <small>
                Select the domain API provider for availability checks.
              </small>
            </div>
            {domainSettings.domain_api_provider === "godaddy" && (
              <>
                <div className="setting-card">
                  <h4>🔑 GoDaddy API Key</h4>
                  <input
                    type="text"
                    value={domainSettings.godaddy_api_key || ""}
                    onChange={(event) =>
                      setDomainSettings({
                        ...domainSettings,
                        godaddy_api_key: event.target.value,
                      })
                    }
                    placeholder="Enter GoDaddy API Key"
                  />
                  <small>Get your API key from GoDaddy Developer Portal.</small>
                </div>
                <div className="setting-card">
                  <h4>🔐 GoDaddy API Secret</h4>
                  <input
                    type="password"
                    value={domainSettings.godaddy_api_secret || ""}
                    onChange={(event) =>
                      setDomainSettings({
                        ...domainSettings,
                        godaddy_api_secret: event.target.value,
                      })
                    }
                    placeholder="Enter GoDaddy API Secret"
                  />
                  <small>
                    Keep this secure. It will not be displayed again.
                  </small>
                </div>
              </>
            )}
            {domainSettings.domain_api_provider === "hostafrica" && (
              <div className="setting-card">
                <h4>🔑 HostAfrica API Key</h4>
                <input
                  type="text"
                  value={domainSettings.hostafrica_api_key || ""}
                  onChange={(event) =>
                    setDomainSettings({
                      ...domainSettings,
                      hostafrica_api_key: event.target.value,
                    })
                  }
                  placeholder="Enter HostAfrica API Key"
                />
                <small>Get your API key from HostAfrica control panel.</small>
              </div>
            )}
            <div className="setting-card summary-card">
              <h4>📊 Current Status</h4>
              <div className="status-summary">
                <div className="status-item">
                  <span className="status-label">Availability Check:</span>
                  <span
                    className={`status-value ${domainSettings.enable_availability_check === "true" ? "enabled" : "disabled"}`}
                  >
                    {domainSettings.enable_availability_check === "true"
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">DNS Configuration:</span>
                  <span
                    className={`status-value ${domainSettings.enable_dns_config === "true" ? "enabled" : "disabled"}`}
                  >
                    {domainSettings.enable_dns_config === "true"
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">API Provider:</span>
                  <span className="status-value">
                    {domainSettings.domain_api_provider || "None"}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Auto-DNS:</span>
                  <span
                    className={`status-value ${domainSettings.auto_configure_dns === "true" ? "enabled" : "disabled"}`}
                  >
                    {domainSettings.auto_configure_dns === "true"
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="domain-settings-actions">
            <button
              className="btn-save-domain"
              type="button"
              onClick={saveConfiguration}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Domain Settings"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default WebsiteOnboardingControl;
